// The kiosk home screen. Designed to be read from across the kitchen and
// used with one tap: what's due now, big log buttons, what's coming up.

import { AppPromo } from './AppPromo.tsx';
import {
  Bone,
  Cctv,
  Brush,
  CalendarClock,
  ChevronRight,
  CloudRain,
  Lightbulb,
  X,
  Droplet,
  Footprints,
  GraduationCap,
  HeartPulse,
  House,
  Moon,
  NotebookPen,
  Plus,
  Scale,
  Sparkles,
  StickyNote,
  Sun,
  ToyBrick,
  TriangleAlert,
  Utensils,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { SOCIAL_CATEGORIES } from '../../content/socialisation.ts';
import { SKILLS } from '../../content/training.ts';
import { deleteEvent, logEvent, openDuration, saveProfile } from '../../data/actions.ts';
import { useCollection, useDevice, useEvents, useHousehold, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { formatMiles } from '../../domain/geo.ts';
import { toast } from '../../ui/toast.tsx';
import { finishWalk, gpsAvailable, startWalk } from '../walks/gps.ts';
import { careState, openCare } from '../../domain/care.ts';
import { describeMeal } from '../../domain/feeding.ts';
import { seasonalAlerts } from '../../domain/milestones.ts';
import { accidentFreeSince, dayStats, toiletSuccess } from '../../domain/status.ts';
import { formatDuration, formatTime, inClockWindow, relativeDay, startOfDay, MINUTE } from '../../domain/time.ts';
import type { LogEvent } from '../../domain/types.ts';
import { EVENT_META, describeEvent } from '../../app/eventMeta.ts';
import { navigate } from '../../app/router.ts';
import { Avatar } from '../../ui/bits.tsx';
import { Badge, Card, CardHeader, Ring, Sheet, type Tone } from '../../ui/primitives.tsx';
import { openEditor, openNew } from '../log/EventEditor.tsx';
import { quickAccident, quickMeal, quickPoo, quickSimple, quickWee, toggleSleep, toggleTimed } from '../log/quick.ts';
import { Countdown } from './Countdown.tsx';
import { WeekOneCard } from '../homecoming/WeekOne.tsx';
import { inWeekOne, planDay } from '../../domain/homecoming.ts';
import { validArrival } from '../../domain/firsts.ts';
import { useBrief, useTips } from '../../data/live.ts';
import { AskHero } from '../voice/AskHero.tsx';
import { PendingWalks } from '../walks/PendingWalks.tsx';
import { CatchUp } from '../log/CatchUp.tsx';
import { toiletIntervalMinutes } from '../../domain/age.ts';
import { HealthWatchBanner } from '../health/HealthWatch.tsx';
import { TonightCard } from './Tonight.tsx';
import { HandoverCard } from './Handover.tsx';
import { useMoreBelow } from '../../ui/useMoreBelow.ts';
import { apiUrl } from '../../data/api.ts';
import { apptPlace, countdown, needsWriteUp, upcomingAppointments, whenLabel } from '../../domain/appointments.ts';
import { openAppointment, writeUp } from '../health/Appointments.tsx';
import { logTrainingSession, useTrainingWeek } from '../../data/training.ts';
import { SESSIONS_PER_DAY, planTitle, weekProgress } from '../../domain/trainingplan.ts';

export function Today() {
  const status = useLiveStatus(15_000);
  const profile = useProfile();
  if (!status.isHome) return <Countdown />;
  const weekOne = inWeekOne(planDay(profile.homeDate, status.now));
  return (
    <div className="today">
      <div className="today-main">
        <AppPromo />
        <ArrivalCard />
        <HealthWatchBanner />
        <HandoverCard />
        <NowCard />
        <QuickLog />
      </div>
      <div className="today-side">
        {weekOne ? <WeekOneCard /> : <BriefCard />}
        <TonightCard />
        <UpNext />
        <TodayFeed />
      </div>
    </div>
  );
}

// --- Pick-up day -------------------------------------------------------------------

/** Pick-up day, until you tap it: the button for the moment he comes through the door. */
function ArrivalCard() {
  const profile = useProfile();
  const s = useLiveStatus(60_000);
  const guest = !!useDevice().guest;
  if (guest || validArrival(profile.homeDate, profile.arrivedAt) !== undefined || planDay(profile.homeDate, s.now) !== 0) return null;
  const name = profile.name || 'Arlo';
  return (
    <section className="arrival">
      <span className="arrival-photo">
        <img src={profile.photo ?? './arlo.jpg'} alt="" />
      </span>
      <div className="arrival-text">
        <span className="eyebrow">Today’s the day</span>
        <strong>{name} comes home today</strong>
        <span className="muted">Tap the button the moment he comes through the door — it starts his clock, and tells everyone.</span>
      </div>
      <button
        type="button"
        className="btn btn-primary btn-lg arrival-btn"
        onClick={() => {
          const at = Date.now();
          saveProfile({ arrivedAt: at });
          const note = logEvent('note', {}, { at, note: `${name} came home` });
          // A mis-tap shouldn't be forever.
          toast({
            message: `${name}’s home!`,
            detail: 'Both phones get a notification. Tapped too soon? Undo.',
            icon: House,
            tone: 'good',
            actions: [
              {
                label: 'Undo',
                onClick: () => {
                  saveProfile({ arrivedAt: undefined });
                  deleteEvent(note.id);
                },
              },
            ],
            duration: 60_000,
          });
        }}
      >
        <House className="btn-icon" aria-hidden />
        <span>He’s home!</span>
      </button>
    </section>
  );
}

// --- Now --------------------------------------------------------------------------

function NowCard() {
  const s = useLiveStatus(15_000);
  const household = useHousehold();
  const events = useEvents();
  const { toilet, sleep, meals, now, weeks } = s;
  const flat = household.home?.type === 'flat';
  const [catchUp, setCatchUp] = useState(false);
  const aloneOpen = events.find((e) => e.type === 'alone' && e.end === undefined);
  const stats = useMemo(() => dayStats(events, now), [events, now]);
  const success = toiletSuccess(stats);
  const cleanSince = accidentFreeSince(events);

  let tone: Tone = 'good';
  let title: ReactNode;
  let detail: ReactNode;
  let ring = 0;
  let ringLabel: ReactNode = null;

  if (sleep.asleep && sleep.current) {
    tone = 'sleep';
    const mins = now - sleep.current.at;
    title = 'Sleeping';
    detail =
      mins > 3 * 60 * MINUTE && !inClockWindow(now, household.nightStart, household.nightEnd)
        ? 'Still asleep after 3+ hours? If he’s actually up, tap “Woke up” so reminders restart.'
        : 'Let him sleep. When he wakes, straight outside to his spot.';
    ring = Math.min(1, mins / (120 * MINUTE));
    ringLabel = (
      <>
        <strong>{formatDuration(mins)}</strong>
        <span>asleep</span>
      </>
    );
  } else if (toilet.dueAt) {
    const left = toilet.dueAt - now;
    const last = Math.max(toilet.lastWee?.at ?? 0, toilet.lastPoo?.at ?? 0);
    const span = toilet.dueAt - (last || toilet.dueAt - 60 * MINUTE);
    ring = span > 0 ? Math.min(1, Math.max(0, 1 - left / span)) : 1;
    if (toilet.urgency === 'now' || toilet.urgency === 'overdue') {
      tone = toilet.urgency === 'overdue' ? 'bad' : 'warn';
      title = flat ? 'Head out now' : 'Toilet trip now';
      ringLabel = (
        <>
          <strong>Now</strong>
          <span>{left < -MINUTE ? `${formatDuration(-left)} over` : 'due'}</span>
        </>
      );
    } else {
      tone = toilet.urgency === 'soon' ? 'warn' : 'good';
      title = `${flat ? 'Head out' : 'Toilet trip'} in ${formatDuration(left, { long: true })}`;
      ringLabel = (
        <>
          <strong>{formatDuration(left)}</strong>
          <span>to go</span>
        </>
      );
    }
    detail = (
      <>
        {toilet.reason}.{' '}
        {flat && (
          <>
            {weeks < 16 ? 'Carry him' : 'Take him'} straight down to {household.home?.toiletSpot || 'his spot'}.{' '}
          </>
        )}
        Cue: <strong>“{household.toiletCue ?? 'Be quick'}”</strong>, then reward the moment he finishes.
      </>
    );
  } else if (toilet.stale) {
    tone = 'neutral';
    title = 'Keep to the routine';
    detail = (
      <>
        {toilet.lastLogAt ? `Nothing logged since ${formatTime(toilet.lastLogAt)} — no problem. ` : 'Nothing logged yet today. '}
        At {s.age?.label ?? 'his age'} he needs to go out about every {toiletIntervalMinutes(weeks)} min while awake, and straight after sleep, food and play.{' '}
        <button type="button" className="link" onClick={() => setCatchUp(true)}>
          Catch up
        </button>{' '}
        or just tap the next thing that happens.
      </>
    );
    ringLabel = (
      <>
        <strong>~{toiletIntervalMinutes(weeks)}m</strong>
        <span>rhythm</span>
      </>
    );
  } else {
    title = 'All good';
  }

  const napLine =
    !sleep.asleep && sleep.awakeFor !== undefined
      ? sleep.napUrgency === 'ok' || sleep.napUrgency === 'soon'
        ? `Awake ${formatDuration(sleep.awakeFor)} · nap due ${sleep.napDueAt && sleep.napDueAt > now ? `in ${formatDuration(sleep.napDueAt - now)}` : 'now'}`
        : `Awake ${formatDuration(sleep.awakeFor)} — nap time (overtired puppies get bitey)`
      : undefined;

  return (
    <Card className={`now-card tone-${tone}`}>
      <div className="now-main">
        <Ring value={ring} size={128} stroke={11} tone={tone}>
          {ringLabel}
        </Ring>
        <div className="now-text">
          <span className="eyebrow">{sleep.asleep ? 'Right now' : 'Toilet training'}</span>
          <h2 className="now-title">{title}</h2>
          <p className="now-detail">{detail}</p>
          {aloneOpen && (
            <p className="now-nap">
              <House aria-hidden /> Home alone for {formatDuration(now - aloneOpen.at)}
              {aloneOpen.source === 'automation' ? ' (you’re both out)' : ''} ·{' '}
              <a className="link" href="#/camera">
                camera
              </a>
            </p>
          )}
          {napLine && (
            <p className={`now-nap${sleep.napUrgency === 'now' || sleep.napUrgency === 'overdue' ? ' warn' : ''}`}>
              <Moon aria-hidden /> {napLine}
            </p>
          )}
        </div>
      </div>
      <div className="now-stats">
        <Stat label="Since wee" value={toilet.lastWee ? formatDuration(now - toilet.lastWee.at) : '—'} />
        <Stat label="Since poo" value={toilet.lastPoo ? formatDuration(now - toilet.lastPoo.at) : '—'} />
        <Stat label="Meals" value={`${meals.eatenToday} / ${meals.perDay}`} />
        <Stat label="Outside today" value={success !== undefined ? `${Math.round(success * 100)}%` : '—'} />
        <Stat
          label="Accident-free"
          value={cleanSince ? formatDuration(now - cleanSince) : '—'}
          hint={weeks < 16 ? 'Accidents are normal for months' : undefined}
        />
      </div>
      <CatchUp open={catchUp} onClose={() => setCatchUp(false)} since={toilet.lastLogAt} />
    </Card>
  );
}

function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="stat" title={hint}>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

// --- Morning brief -------------------------------------------------------------------

function BriefCard() {
  const b = useBrief();
  return (
    <Card className="brief-card">
      <div className="brief-head">
        <span className="eyebrow">{b.via === 'ai' ? 'Today’s brief · written for you' : 'Today’s brief'}</span>
        <button type="button" className="link small" onClick={() => navigate('/diary')}>
          His week <ChevronRight aria-hidden width={14} />
        </button>
      </div>
      <strong className="brief-title">{b.title}</strong>
      <p className="brief-body">{b.body}</p>
    </Card>
  );
}

// --- Quick log -----------------------------------------------------------------------

function QuickLog() {
  const s = useLiveStatus(15_000);
  const device = useDevice();
  useEvents(); // re-render when durations open/close
  const [more, setMore] = useState(false);
  const [accident, setAccident] = useState(false);
  const walking = openDuration('walk');
  const playing = openDuration('play');
  const next = s.meals.next;

  return (
    <Card className="quick-card">
      <AskHero />
      <div className="quick-grid">
        <QuickButton icon={Droplet} label="Wee" sub="outside" tone="warn" onClick={quickWee} />
        <QuickButton icon={Sparkles} label="Poo" sub="outside" tone="accent" onClick={quickPoo} />
        <QuickButton icon={TriangleAlert} label="Accident" sub="indoors" tone="bad" onClick={() => setAccident(true)} />
        <QuickButton
          icon={Utensils}
          label="Meal"
          sub={next ? `${next.grams ? `${next.grams} g · ` : ''}${next.time}` : 'all done today'}
          tone="good"
          onClick={() => quickMeal(next?.grams)}
        />
        <QuickButton
          icon={s.sleep.asleep ? Sun : Moon}
          label={s.sleep.asleep ? 'Woke up' : 'Nap'}
          sub={s.sleep.asleep && s.sleep.current ? `asleep ${formatDuration(s.now - s.sleep.current.at)}` : 'start sleep'}
          tone="sleep"
          active={s.sleep.asleep}
          onClick={toggleSleep}
        />
        <QuickButton
          icon={Footprints}
          label={walking ? 'Back home' : 'Outing'}
          sub={walking ? formatDuration(s.now - walking.at) : s.weeks < 12 ? 'carry him out' : 'walk'}
          tone="good"
          active={!!walking}
          onClick={() => {
            if (walking) {
              const w = finishWalk();
              if (w?.end) toast({ message: `Back home · ${formatDuration(w.end - w.at)}`, detail: w.type === 'walk' && w.data.distanceKm ? formatMiles(w.data.distanceKm) : undefined, tone: 'good', icon: Footprints });
            } else {
              startWalk(!device.kiosk && gpsAvailable);
              toast({ message: 'Outing started', detail: !device.kiosk && gpsAvailable ? 'Recording distance — open Walks for the live view.' : undefined, tone: 'good', icon: Footprints, actions: [{ label: 'Live view', onClick: () => navigate('/walks') }] });
            }
          }}
        />
        <QuickButton
          icon={ToyBrick}
          label={playing ? 'Stop play' : 'Play'}
          sub={playing ? formatDuration(s.now - playing.at) : 'start'}
          tone="accent"
          active={!!playing}
          onClick={() => toggleTimed('play')}
        />
        <QuickButton icon={GraduationCap} label="Training" sub="log session" tone="info" onClick={() => openNew('training', { skills: [] })} />
        <QuickButton icon={Sparkles} label="New thing" sub="socialise" tone="good" onClick={() => openNew('social', { itemId: '', reaction: 'happy' }, { title: 'New experience' })} />
        <QuickButton icon={Plus} label="More" sub="weight, meds…" tone="neutral" onClick={() => setMore(true)} />
      </div>
      <Sheet open={accident} onClose={() => setAccident(false)} title="Accident indoors">
        <p className="muted">No telling off — he doesn’t understand yet. Interrupt calmly if you catch him mid-way and pop him outside.</p>
        <div className="quick-grid quick-grid-2">
          <QuickButton icon={Droplet} label="Wee" tone="warn" onClick={() => (quickAccident('wee'), setAccident(false))} />
          <QuickButton icon={Sparkles} label="Poo" tone="accent" onClick={() => (quickAccident('poo'), setAccident(false))} />
        </div>
      </Sheet>
      <Sheet open={more} onClose={() => setMore(false)} title="Log something else">
        <div className="quick-grid quick-grid-3">
          {(
            [
              [Scale, 'Weight', () => openNew('weight', { kg: 0 })],
              [Brush, 'Grooming', () => openNew('groom', { tasks: ['brush'] })],
              [House, 'Alone time', () => toggleTimed('alone')],
              [Bone, 'Chew / Kong', () => quickSimple('treat')],
              [HeartPulse, 'Health', () => openNew('health', { symptom: 'other', severity: 1 })],
              [CalendarClock, 'Medication', () => openNew('meds', { name: '' })],
              [StickyNote, 'Note', () => openNew('note', {})],
              [Moon, 'Past nap', () => openNew('sleep', {}, { at: Date.now() - 60 * MINUTE, end: Date.now() - 15 * MINUTE })],
              [Droplet, 'Past wee/poo', () => openNew('wee', { where: 'outside' })],
            ] as [LucideIcon, string, () => void][]
          ).map(([icon, label, fn]) => (
            <QuickButton key={label} icon={icon} label={label} tone="neutral" onClick={() => (setMore(false), fn())} />
          ))}
        </div>
      </Sheet>
    </Card>
  );
}

function QuickButton({ icon: Icon, label, sub, tone, onClick, active }: { icon: LucideIcon; label: string; sub?: string; tone: Tone; onClick: () => void; active?: boolean }) {
  return (
    <button type="button" className={`quick tone-${tone}${active ? ' active' : ''}`} onClick={onClick}>
      <span className="quick-icon">
        <Icon aria-hidden />
      </span>
      <span className="quick-label">{label}</span>
      {sub && <span className="quick-sub">{sub}</span>}
    </button>
  );
}

// --- Up next ------------------------------------------------------------------------------

interface NextItem {
  key: string;
  icon: LucideIcon;
  title: string;
  detail?: string;
  tone: Tone;
  badge?: string;
  onClick?: () => void;
  onDismiss?: () => void;
}

function UpNext() {
  const s = useLiveStatus(60_000);
  const care = useCollection('care');
  const skills = useCollection('skills');
  const events = useEvents();
  const profile = useProfile();

  const { tips, dismiss } = useTips(2);
  const appts = useCollection('appointments');
  const contacts = useCollection('contacts');
  const plan = useTrainingWeek();
  const guest = !!useDevice().guest;
  const items = useMemo(() => {
    const out: NextItem[] = [];
    // Booked appointments in the next day and a half, then any vet visit waiting to be written up.
    for (const a of upcomingAppointments(appts, s.now).filter((a) => a.at - s.now < 36 * 60 * MINUTE)) {
      const soon = countdown(a.at, s.now);
      const place = apptPlace(a, contacts);
      out.push({
        key: `appt-${a.id}`,
        icon: CalendarClock,
        title: `${a.title} · ${whenLabel(a.at, s.now)}`,
        detail: [place, a.notes].filter(Boolean).join(' — ') || undefined,
        tone: a.at - s.now < 2 * 60 * MINUTE ? 'warn' : 'info',
        badge: a.at <= s.now ? 'now' : soon,
        onClick: () => openAppointment({}, a),
      });
    }
    for (const a of needsWriteUp(appts, s.now).slice(0, 1))
      out.push({ key: `writeup-${a.id}`, icon: NotebookPen, title: `How did it go? ${a.title}`, detail: 'Write down what the vet said, what was given and the cost.', tone: 'warn', onClick: () => writeUp(a, apptPlace(a, contacts)) });
    out.push(...tips.map((t) => ({
      key: `tip-${t.id}`,
      icon: t.kind === 'weather' ? CloudRain : Lightbulb,
      title: t.title,
      detail: t.body,
      tone: 'accent' as Tone,
      onClick: t.route ? () => navigate(t.route!) : undefined,
      onDismiss: () => dismiss(t.id),
    })));
    if (s.meals.next) {
      const m = s.meals.next;
      const late = m.at < s.now - 30 * MINUTE;
      const probablyForgot = m.at < s.now - 90 * MINUTE;
      out.push(
        probablyForgot
          ? {
              key: 'meal',
              icon: Utensils,
              title: `${describeMeal(m, s.meals.perDay).split(' · ')[0]} not logged`,
              detail: `Did he have it around ${m.time}? Tap to log it as eaten.`,
              tone: 'neutral',
              onClick: () => {
                logEvent('meal', { grams: m.grams, appetite: 'all' }, { at: m.at });
                toast({ message: 'Meal logged', tone: 'good', icon: Utensils });
              },
            }
          : {
              key: 'meal',
              icon: Utensils,
              title: describeMeal(m, s.meals.perDay),
              detail: m.grams ? `${m.grams} g · meal ${m.index + 1} of ${s.meals.perDay}` : 'Set daily grams on the Food page',
              tone: late ? 'warn' : 'good',
              badge: late ? 'due' : undefined,
              onClick: () => navigate('/food'),
            },
      );
    }
    // A care job that's booked as an appointment shows once, as the appointment.
    const booked = new Set(upcomingAppointments(appts, s.now).flatMap((a) => a.careIds ?? []));
    const dueCare = openCare(care).filter((c) => !booked.has(c.id) && !['upcoming', 'unscheduled'].includes(careState(c, s.now)));
    const overdue = dueCare.filter((c) => careState(c, s.now) === 'overdue');
    if (overdue.length > 2)
      out.push({
        key: 'overdue',
        icon: HeartPulse,
        title: `${overdue.length} care jobs to tick off`,
        detail: overdue
          .slice(0, 3)
          .map((c) => c.title)
          .join(' · '),
        tone: 'warn',
        badge: 'catch up',
        onClick: () => navigate('/health'),
      });
    for (const c of dueCare.filter((c) => overdue.length <= 2 || !overdue.includes(c)).slice(0, 4)) {
      const st = careState(c, s.now);
      out.push({
        key: c.id,
        icon: HeartPulse,
        title: c.title,
        detail: c.due ? relativeDay(c.due, s.now) : undefined,
        tone: st === 'overdue' ? 'bad' : st === 'due' ? 'warn' : 'info',
        badge: st === 'overdue' ? 'overdue' : st === 'due' ? 'today' : undefined,
        onClick: () => navigate('/health'),
      });
    }
    // This week's training plan: tap after a session to log it.
    if (plan) {
      const p = weekProgress(plan, events, s.now);
      const done = p.today >= SESSIONS_PER_DAY;
      out.push({
        key: 'train',
        icon: GraduationCap,
        title: `Training: ${planTitle(plan)}`,
        detail: done ? `All ${SESSIONS_PER_DAY} sessions done today — nice. ${p.total} this week.` : `${p.today} of ${SESSIONS_PER_DAY} five-minute sessions today — tap when you’ve done one.`,
        tone: done ? 'good' : 'info',
        badge: `${Math.min(p.today, SESSIONS_PER_DAY)}/${SESSIONS_PER_DAY}`,
        onClick: guest
          ? () => navigate('/training')
          : () => {
              const e = logTrainingSession(plan);
              toast({ message: 'Training session logged', detail: planTitle(plan), tone: 'good', icon: GraduationCap, actions: [{ label: 'Undo', onClick: () => deleteEvent(e.id) }] });
            },
      });
    } else {
      // Before the week's plan exists (e.g. before he's home): the first skill for his age.
      const levels = new Map(skills.map((k) => [k.id, k.level]));
      const focus = SKILLS.filter((k) => k.fromWeeks <= s.weeks + 1 && (levels.get(k.id) ?? 0) < 3).slice(0, 1)[0];
      if (focus) out.push({ key: 'train', icon: GraduationCap, title: `Practise: ${focus.name}`, detail: focus.summary, tone: 'info', onClick: () => navigate(`/training/${focus.id}`) });
    }
    // Socialisation idea of the day: something not yet experienced, stable for the day.
    if (s.weeks < 20) {
      const seen = new Set(events.filter((e): e is LogEvent<'social'> => e.type === 'social').map((e) => e.data.itemId));
      const pool = SOCIAL_CATEGORIES.flatMap((c) => c.items).filter((i) => !seen.has(i.id) && (s.weeks >= 12 || !i.afterVaccination));
      if (pool.length) {
        const pick = pool[Math.floor(startOfDay(s.now) / 86_400_000) % pool.length];
        out.push({ key: 'social', icon: Sparkles, title: `Try today: ${pick.label}`, detail: pick.tip, tone: 'good', onClick: () => navigate('/social') });
      }
    }
    const season = seasonalAlerts(s.now)[0];
    if (season) out.push({ key: season.id, icon: TriangleAlert, title: season.title, detail: season.detail, tone: season.level === 'warn' ? 'warn' : 'info', onClick: () => navigate('/guide') });
    return out;
  }, [s, care, skills, events, tips, appts, contacts, plan, guest]);
  const nextMore = useMoreBelow<HTMLUListElement>([items.length]);

  return (
    <Card className="upnext-card">
      <CardHeader title="Up next" icon={CalendarClock} sub={profile.dob ? undefined : 'Add his date of birth in Settings for a full plan'} />
      <PendingWalks />
      <ul className={`next-list${nextMore.more ? ' has-more' : ''}`} ref={nextMore.ref}>
        {items.map((i) => (
          <li key={i.key}>
            <button type="button" className={`next-item tone-${i.tone}`} onClick={i.onClick}>
              <span className="next-icon">
                <i.icon aria-hidden />
              </span>
              <span className="next-text">
                <strong>{i.title}</strong>
                {i.detail && <span className="muted">{i.detail}</span>}
              </span>
              {i.badge && <Badge tone={i.tone}>{i.badge}</Badge>}
              {!i.onDismiss && <ChevronRight className="chev" aria-hidden />}
            </button>
            {i.onDismiss && (
              <button type="button" className="next-dismiss" aria-label="Dismiss tip" onClick={i.onDismiss}>
                <X aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

// --- Today's feed -------------------------------------------------------------------------

function TodayFeed() {
  const events = useEvents();
  const household = useHousehold();
  const s = useLiveStatus(60_000);
  const since = startOfDay(s.now) - (new Date(s.now).getHours() < 6 ? 6 * 60 * MINUTE : 0);
  const today = events.filter((e) => e.at >= since && e.type !== 'tracker');
  const feedMore = useMoreBelow<HTMLOListElement>([today.length]);
  return (
    <Card className="feed-card">
      <CardHeader title="Today" icon={CalendarClock} action={<a href="#/timeline" className="link">All</a>} />
      {today.length === 0 ? (
        <p className="muted feed-empty">Nothing logged yet today. Tap a button when something happens — it all adds up to a picture of his routine.</p>
      ) : (
        <ol className={`feed${feedMore.more ? ' has-more' : ''}`} ref={feedMore.ref}>
          {today.map((e) => (
            <FeedRow key={e.id} e={e} person={household.people.find((p) => p.id === e.by)} />
          ))}
        </ol>
      )}
    </Card>
  );
}

export function FeedRow({ e, person }: { e: LogEvent; person?: import('../../domain/types.ts').Person }) {
  const meta = EVENT_META[e.type];
  const d = describeEvent(e, {
    skill: (id) => SKILLS.find((k) => k.id === id)?.name,
    social: (id) => SOCIAL_CATEGORIES.flatMap((c) => c.items).find((i) => i.id === id)?.label,
  });
  const accident = (e.type === 'wee' || e.type === 'poo') && e.data.where === 'inside';
  return (
    <li>
      <button type="button" className={`feed-row tone-${accident ? 'bad' : meta.tone}`} onClick={() => openEditor(e)}>
        <span className="feed-time">{formatTime(e.at)}</span>
        <span className="feed-icon">
          <meta.icon aria-hidden />
        </span>
        <span className="feed-text">
          <strong>{d.title}</strong>
          {(d.detail || e.note) && <span className="muted">{[d.detail, e.note].filter(Boolean).join(' · ')}</span>}
        </span>
        {person && <Avatar person={person} size={26} />}
      </button>
      {e.type === 'camera' && <CameraExtras e={e} />}
    </li>
  );
}

/** A detection's frame and a link to its clip, when the camera system gave us them. */
function CameraExtras({ e }: { e: LogEvent<'camera'> }) {
  const household = useHousehold();
  const [broken, setBroken] = useState(false);
  const clip = clipLink(e, household.protectLink);
  const src = e.data.snapshot && e.data.snapshotToken && !broken ? apiUrl(`/api/photos/${e.data.snapshot}?t=${encodeURIComponent(e.data.snapshotToken)}`) : undefined;
  if (!clip && !src) return null;
  return (
    <div className="feed-cam">
      {src && (
        <a href={src} target="_blank" rel="noreferrer" className="feed-cam-shot">
          <img src={src} alt={`What the camera saw at ${formatTime(e.at)}`} loading="lazy" onError={() => setBroken(true)} />
        </a>
      )}
      {clip && (
        <a href={clip} target="_blank" rel="noreferrer" className="btn btn-soft btn-sm">
          <Cctv className="btn-icon" aria-hidden />
          <span>Watch clip</span>
        </a>
      )}
    </div>
  );
}

/** The clip in UniFi Protect: via the console's web address if set (works anywhere), else the link Protect sent. */
export function clipLink(e: LogEvent<'camera'>, base?: string): string | undefined {
  const web = (s?: string) => (s && /^https?:\/\//.test(s) ? s : undefined);
  if (web(base) && e.data.eventId) return `${base!.replace(/\/+$/, '')}/protect/events/event/${encodeURIComponent(e.data.eventId)}`;
  return e.source === 'automation' ? web(e.data.clip) : undefined;
}
